import MaterialBuilder from "../rendering/material-builder";
import RenderManager from "../rendering/render-manager";
import SlideManager from "../utils/slide-manager";
import FBXLoader from "../loaders/fbx-loader";

import { listen, fire } from "../utils/events";
import getPersistentUrl from "../utils/persistent-path";



const fontUrl = '/Q3/fonts/DINPro-Regular.woff';
const soundUrl = 'interactiveCar/engine.mp3';

const UvScale = 0.25;

const Colors = {
    terrain: 0x625a3e,
    //terrainSide: 0x805636,

    graph: {
        origin: 0xaaaaaa,
        xAxis: 0xaa0000,
        yAxis: 0x0000aa,
        userAxis: 0xaaaaaa,
        dragPoint: 0x111111,
        highlightedDragPoint: 0x00aa00,
        projection: 0xaaaa00,
        projectionPoint: 0xaa2200,
    },
};



const modelUrlBase = "interactiveCar/";

const CarModels = {
    jeep: {
        modelName: "jeep/Jeep-Cooboo.fbx",
        textures: {
            color: "jeep/Jeep-Rework_Jeep_substance_BaseColor.jpg",
        },
        material: {
            color: 0xffffff,
            parameters: {},
            textures: {
                map: "color",
            },
        },
        parameters: {
            wheelNames: [
                'Tube05',
                'Tube06',
                'Tube07',
                'Tube08',
            ],
            modelScale: 0.001,
            modelRotation: new THREE.Euler(0, -Math.PI * 0.5, 0),
            rearWheels: [3, 2],
            frontWheels: [0, 1],
            wheelPivotOffsets: [1, -1, -1, 1].map(x => x * 1.573),
        },
    },
    /*jeepHighQuality: {
        modelName: "jeep/Jeep-Cooboo.fbx",
        textures: {
            color: "jeep/Jeep-Rework_Jeep_substance_BaseColor.png",
            metallic: "jeep/Jeep-Rework_Jeep_substance_Metallic.png",
            normal: "jeep/Jeep-Rework_Jeep_substance_Normal.png",
            roughness: "jeep/Jeep-Rework_Jeep_substance_Roughness.png",
        },
        material: {
            color: 0xffffff,
            parameters: {
                roughness: 1,
                metalness: 1,
            },
            textures: {
                map: "color",
                normalMap: "normal",
                roughnessMap: "roughness",
                metalnessMap: "metallic",
            },
        },
        parameters: {
            wheelNames: [
                'Tube05',
                'Tube06',
                'Tube07',
                'Tube08',
            ],
            modelScale: 0.001,
            modelRotation: new THREE.Euler(0, -Math.PI*0.5, 0),
            rearWheels: [3, 2],
            frontWheels: [0, 1],
            wheelPivotOffsets: [1, -1, -1, 1].map(x => x*1.573),
        },
    },*/
    testBox: {
        modelName: "test-box/car.fbx",
        textures: {
            color: "test-box/color.png",
        },
        material: {
            color: 0xffffff,
            parameters: {},
            textures: {
                map: "color",
            },
        },
        parameters: {
            wheelNames: [
                'Cylinder',
                'Cylinder001',
                'Cylinder002',
                'Cylinder003',
            ],
            modelScale: 0.1,
            modelRotation: new THREE.Euler(0, 0, 0),
            rearWheels: [2, 3],
            frontWheels: [1, 0],
            wheelPivotOffsets: [1, -1, -1, 1].map(x => x * 0),
        },
    },
};



const groundTextureName = "Grass Essentials Dirt Texture (2).jpg";
//const sideTextureName = "red_dirt_mud_01_diff_4k.jpg";



const allTerrainCars = new Set();

function updateTerrainCars(mouse, camera, controls) {
    let anyVisible = false;

    for (let terrainCar of allTerrainCars) {
        let thisVisible = true;

        for (let object = terrainCar; object; object = object.parent) {
            if (!object.visible) {
                thisVisible = false;
                break;
            }
        }

        if (thisVisible) {
            anyVisible = true;
            break;
        }
    }

    if (anyVisible) RenderManager.activeCamera.update = true;

    for (let terrainCar of allTerrainCars) {
        if (terrainCar.uuid in Q3.data.assets.objects) {
            terrainCar.updateInput(mouse, camera, controls);
        } else {
            allTerrainCars.delete(terrainCar);
            terrainCar.dispose();
        }
    }
}

function assignPrimitiveRecursive(target, source) {
    for (let key in source) {
        let sourceVal = source[key];

        if (typeof sourceVal === 'object') {
            let targetVal = target[key];

            if (typeof targetVal !== 'object') {
                targetVal = sourceVal instanceof Array ? [] : {};
                target[key] = targetVal;
            }

            assignPrimitiveRecursive(targetVal, sourceVal);
        } else {
            target[key] = sourceVal;
        }
    }
};

class TerrainCar extends THREE.Object3D {
    constructor(params) {
        super();

        this.isTerrainCar = true;
        this.type = "TerrainCar";

        this._params = {
            terrain: {
                size: [32, 32],
                segmentCount: 64,
                shape: {
                    frequency: [0.5, 0.5],
                    zScale: 0.5,
                },
                boundaryDistance: 5,
            },
            car: {
                model: "jeep",
                initialSpeed: 20 / 108,
                initialSteering: 0,
                initialPosition: [0, 0],
                initialDirection: 0,
                maxSpeed: 0.46296296296296297,
                maxSteer: 0.7853981633974483,
                acceleration: 0.005,
                scale: 0.1,
            },
            ui: {
                visible: {
                    steeringWheel: true,
                    throttle: true,
                    speedDial: true,
                    velXDial: true,
                    velYDial: true,
                    velUserAxisDial: true,
                    carCompass: true,
                    userAxisCompass: true,
                }
            },
            graph: {
                zOffset: 3.5,
                visible: true,
                projectionVisible: true,
                axisVisible: {
                    x: true,
                    y: true,
                    user: true,
                },
                style: {
                    originRadius: 0.25,
                    axisRadius: 0.25,
                    axisTipRadius: 0.25,
                    axisTipHeight: 1,
                    projectionLineRadius: 0.125,
                    projectionPointRadius: 0.25,
                    userDragPointRadius: 0.75,
                },
            },
            labels: {
                visible: {
                    speed: true,
                    velocityX: true,
                    velocityY: true,
                    velocityUserAxis: true,
                },
            },
            sound: {
                volume: 0.5,
                refDistance: 0.3,
            },
        };
        assignPrimitiveRecursive(this._params, params);

        this._time = 0;

        this._terrain = new Terrain({
            ground: getPersistentUrl(modelUrlBase + groundTextureName),
            //side: modelUrlBase+sideTextureName
        }, {
                ground: Colors.terrain,
                //side: Colors.terrainSide,
            }, this._params.terrain);
        this._buildGraphObjects();
        this._buildCar();
        this.add(this._terrain);

        this._rebuildGraph();
        this._updateGraphStyle();

        this._isMovementBlocked = { x: false, y: false };

        this._terrain.dontShowInHierarchyPanel = true;
        this._car.dontShowInHierarchyPanel = true;
        this._graph.root.dontShowInHierarchyPanel = true;

        let visible = this.visible;
        Object.defineProperty(this, "visible", {
            get: () => visible,
            set: (enabled) => {
                visible = enabled;
                this._overlay.setVisible(visible);
                fire('terraincar_toggle_visibility', this);
            },
        });

        this._controls = {
            steering: this._params.car.initialSteering,
            throttle: this._params.car.initialSpeed / this._params.car.maxSpeed,
        };
        this._position = [this._params.car.initialPosition[0], this._params.car.initialPosition[1]];
        this._direction = this._params.car.initialDirection;
        this._speed = 0;
        this._dirSpeed = { x: 0, y: 0 };

        this._lightOffset = new THREE.Vector3(1, 1, 0);
        this._lightOffset.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 4);
        this._lightNoShadow = new THREE.DirectionalLight(0xffffff, 0.3);
        this._lightShadow = new THREE.DirectionalLight(0xffffff, 0.5);
        this._lightShadow.castShadow = true;
        this._lightNoShadow.dontShowInHierarchyPanel = true;
        this._lightShadow.dontShowInHierarchyPanel = true;
        this._lightNoShadow.target = this._car;
        this._lightShadow.target = this._car;
        this.add(this._lightNoShadow, this._lightShadow);

        this.setCarModel(this._params.car.model);

        this.initInput();

        allTerrainCars.add(this);

        this._initOverlay();
        this._initSound();
    }

    setInitialSpeed(speed) {
        this._params.car.initialSpeed = speed;
        this._controls.throttle = this._params.car.initialSpeed / this._params.car.maxSpeed;
    }

    dispose() {
        this._uninitOverlay();
        this._uninitSound();
    }

    get params() {
        return this._params;
    }

    _buildGraphObjects() {
        this._graph = new Graph();
        this.add(this._graph.root);
    }

    _rebuildGraph() {
        let [sizeX, sizeY] = this._params.terrain.size;
        this._graph.setSize(sizeX, sizeY);
    }

    _buildCar() {
        this._wheelDriveRotation = 0;

        this._car = new Car();
        this.add(this._car);
    }

    setCarModel(name) {
        this._params.car.model = name;

        loadModel(name).then(carModel => {
            if (name !== this._params.car.model) return;

            this._addCarModel(carModel);
        });
    }

    _addCarModel(carModel) {
        this._model = carModel
        this._car.setModel(carModel);

        this._modelInitialized = true;

        this._updateCarSpatialParameters();
    }

    _updateCarSpatialParameters() {
        let carScale = this._params.car.scale;
        this._car.setScale(carScale);
    }

    setCarScale(scale) {
        this._params.car.scale = scale;

        if (this._modelInitialized) {
            this._updateCarSpatialParameters();
        }
    }

    setGraphStyle(style) {
        Object.assign(this._params.graph.style, style);
        this._updateGraphStyle();
        this._updateUserDragPointRadius();
    }

    _updateGraphStyle() {
        this._graph.setStyle(this._params.graph.style);
    }

    _updateGraph() {
        let { visible, projectionVisible, zOffset } = this._params.graph;

        let graph = this._graph;
        graph.root.visible = visible;
        graph.setProjectionVisible(projectionVisible);

        if (!visible) return;

        let [sizeX, sizeY] = this._params.terrain.size;
        let [carX, carY] = this._position;

        graph.root.position.set(-sizeX * 0.5, zOffset, sizeY * 0.5);
        if (projectionVisible) {
            graph.setProjectionPoint(sizeX * 0.5 + carX, sizeY * 0.5 + carY);
        }

        for (let axis in this._params.graph.axisVisible) {
            this._graph.setAxisEnabled(axis, this._params.graph.axisVisible[axis]);
        }
    }

    setAxisEnabled(axis, enabled) {
        this._params.graph.axisVisible[axis] = enabled;
    }

    setTerrainSize(sizeX, sizeY) {
        let size = this._params.terrain.size;
        size[0] = sizeX;
        size[1] = sizeY;
        this._terrain.setSize(sizeX, sizeY);
        this._rebuildGraph();
    }

    setTerrainSegmentCount(count) {
        this._params.terrain.segmentCount = count;
        this._terrain.setSegmentCount(count);
    }

    setTerrainShape(fx, fy, zScale) {
        let shape = this._params.terrain.shape;
        shape.frequency[0] = fx;
        shape.frequency[1] = fy;
        shape.zScale = zScale;
        this._terrain.setShape(fx, fy, zScale);
        this._rebuildGraph();
    }

    setAcceleration(acceleration) {
        this._params.car.acceleration = acceleration;
    }

    setMaxSpeed(maxSpeed) {
        this._params.car.maxSpeed = maxSpeed;
    }

    setMaxSteer(maxSteer) {
        this._params.car.maxSteer = maxSteer;
    }

    setUiVisible(param, visible) {
        this._params.ui.visible[param] = visible;
        fire('terraincar_toggle_ui_param', this);
    }

    setGraphVisible(visible) {
        this._params.graph.visible = visible;
    }

    setGraphProjectionVisible(visible) {
        this._params.graph.projectionVisible = visible;
    }

    setGraphZOffset(offset) {
        this._params.graph.zOffset = offset;
    }

    setSteering(steering) {
        this._controls.steering = steering;
        this._params.car.initialSteering = steering;

        let objectData = Q3.data.assets.objects[this.uuid];
        if (objectData) {
            objectData.interactiveCar.car.initialSteering = steering;
        }

        fire('terraincar_info_changed', this);
    }

    setThrottle(throttle) {
        this._controls.throttle = throttle;
        fire('terraincar_info_changed', this);
    }

    get steering() {
        return this._controls.steering;
    }

    get throttle() {
        return this._controls.throttle;
    }

    _getVelocityAfterClamping(vel = {}) {
        vel.x = this._isMovementBlocked.x ? 0 : this._dirSpeed.x;
        vel.y = this._isMovementBlocked.y ? 0 : this._dirSpeed.y;
        return vel;
    }

    get speed() {
        let x = this.velocityX;
        let y = this.velocityY;
        return Math.sqrt(x * x + y * y);
    }

    get velocityX() {
        return this._isMovementBlocked.x ? 0 : this._dirSpeed.x;
    }

    get velocityY() {
        return this._isMovementBlocked.y ? 0 : this._dirSpeed.y;
    }

    get velocityUserAxis() {
        let { start, end } = this._userAxis;
        let axisDir = new THREE.Vector2(end[0] - start[0], end[1] - start[1]).normalize();
        return axisDir.dot(this._getVelocityAfterClamping());
    }

    get carDirection() {
        return this._direction;
    }

    get userAxisDirection() {
        let { start, end } = this._userAxis;
        return Math.atan2(end[0] - start[0], end[1] - start[1]);
    }

    getCarOnGroundMatrix(x, z, dir) {
        let sin = Math.sin(dir), cos = Math.cos(dir);
        let carMatrix = new THREE.Matrix4().set(
            cos, 0, -sin, x,
            0, 1, 0, 0,
            sin, 0, cos, -z,
            0, 0, 0, 1,
        );

        let { rearWheels, frontWheels } = this._model;
        let wheelRL = rearWheels[0], wheelRR = rearWheels[1];
        let wheelFL = frontWheels[0], wheelFR = frontWheels[1];

        let absWheelPos = [];
        for (let pos of this._car.wheelPositions) {
            let groundPos = pos.clone().applyMatrix4(carMatrix);
            groundPos.y = this._terrain.getZ(groundPos.x, -groundPos.z);
            absWheelPos.push(groundPos);
        }

        let dirX = new THREE.Vector3().add(absWheelPos[wheelRR]).add(absWheelPos[wheelFR]).sub(absWheelPos[wheelRL]).sub(absWheelPos[wheelFL]);
        let dirZ = new THREE.Vector3().add(absWheelPos[wheelRL]).add(absWheelPos[wheelRR]).sub(absWheelPos[wheelFL]).sub(absWheelPos[wheelFR]);
        let dirY = new THREE.Vector3().crossVectors(dirZ, dirX).normalize();

        let slopeRotation = new THREE.Vector3(0, 1, 0).cross(dirY);
        let slopeAngleSin = Math.min(Math.max(-1, slopeRotation.length()), 1);
        let slopeAngle = Math.asin(slopeAngleSin);
        let slopeAxis = slopeRotation.clone().divideScalar(slopeAngleSin);

        if (isFinite(slopeAxis.dot(slopeAxis))) {
            let slopeMatrix = new THREE.Matrix4().makeRotationAxis(slopeAxis, slopeAngle);
            let carRotation = new THREE.Matrix4().set(
                cos, 0, -sin, 0,
                0, 1, 0, 0,
                sin, 0, cos, 0,
                0, 0, 0, 1,
            );
            carMatrix.copy(slopeMatrix).multiply(carRotation);
            carMatrix.elements[12] = x;
            carMatrix.elements[14] = -z;
        }

        let y = 0;
        for (let pos of absWheelPos) {
            y += pos.y;
        }
        y /= 4;

        carMatrix.elements[13] = y + (this._car.wheelRadius + this._car.originAboveAxles) / dirY.y;

        return carMatrix;
    }

    _clampPosition() {
        let size = this._params.terrain.size;
        let pos = this._position;

        let clampDistance = this._params.terrain.boundaryDistance * this._params.car.scale;

        let axisName = ['x', 'y'];

        for (let i = 0; i < 2; i++) {
            let sizeH = Math.max(0, size[i] / 2 - clampDistance);
            let pos_i = pos[i];
            if (pos_i < -sizeH) {
                pos_i = -sizeH;
                this._isMovementBlocked[axisName[i]] = true;
            } else if (pos_i > sizeH) {
                pos_i = sizeH;
                this._isMovementBlocked[axisName[i]] = true;
            } else {
                this._isMovementBlocked[axisName[i]] = false;
            }
            pos[i] = pos_i;
        }
    }

    setBoundaryDistance(distance) {
        this._params.terrain.boundaryDistance = distance;
    }

    update() {
        this._updateGraph();

        if (!this._modelInitialized) return;

        let { acceleration, maxSpeed, maxSteer } = this._params.car;

        let axleDistance = this._car.rearAxlePosition.z - this._car.frontAxlePosition.z;

        let targetSpeed = this._controls.throttle * maxSpeed;
        let diffSpeed = targetSpeed - this._speed;
        diffSpeed = Math.min(Math.max(-acceleration, diffSpeed), acceleration);
        this._speed += diffSpeed;

        let dirX0 = Math.sin(this._direction), dirY0 = Math.cos(this._direction);
        this._dirSpeed.x = dirX0 * this._speed;
        this._dirSpeed.y = dirY0 * this._speed;
        this._wheelDriveRotation += this._speed / this._car.wheelRadius;

        let steering = this._controls.steering * maxSteer;
        let turn = Math.tan(steering) * (this._speed / axleDistance);
        this._direction += turn;
        let dirX1 = Math.sin(this._direction), dirY1 = Math.cos(this._direction);

        let [posX, posY] = this._position;
        posX = posX + this._car.rearAxlePosition.z * (dirX1 - dirX0) + this._dirSpeed.x;
        posY = posY + this._car.rearAxlePosition.z * (dirY1 - dirY0) + this._dirSpeed.y;
        this._position[0] = posX;
        this._position[1] = posY;
        this._clampPosition();

        this._car.setWheelDriveRotation(this._wheelDriveRotation);
        this._car.setWheelSteerRotation(steering);

        let matrix = this.getCarOnGroundMatrix(this._position[0], this._position[1], this._direction);
        matrix.decompose(this._car.position, this._car.quaternion, this._car.scale);

        this._updateObjectDataPosition();
        this._updateLightPosition();

        fire('terraincar_info_changed', this);

        RenderManager.activeCamera.update = true;
        RenderManager.shadowMap.needsUpdate = true;
    }

    _updateLightPosition() {
        this._lightNoShadow.position.copy(this._car.position).add(this._lightOffset);
        this._lightShadow.position.copy(this._car.position).add(this._lightOffset);
    }

    _updateObjectDataPosition() {
        let [x, y] = this._position;
        let dir = this._direction;
        this._params.car.initialPosition[0] = x;
        this._params.car.initialPosition[1] = y;
        this._params.car.initialDirection = dir;

        let objectData = Q3.data.assets.objects[this.uuid];
        if (objectData) {
            objectData.interactiveCar.car.initialPosition[0] = x;
            objectData.interactiveCar.car.initialPosition[1] = y;
            objectData.interactiveCar.car.initialDirection = dir;
        }
    }

    initInput() {
        this._draggedPoint = null;
        this._hoveredPoint = null;
        this._mouseDown = false;

        let mat = this._userDragMaterials = {
            main: MaterialBuilder.build({ color: Colors.graph.dragPoint, materialType: "pbr" }),
            highlighted: MaterialBuilder.build({ color: Colors.graph.highlightedDragPoint, materialType: "pbr" }),
        };

        this._userAxis = {
            start: [10, 10],
            end: [20, 30],
        };

        this._userDragObjects = {
            start: new SphereObject(1, mat.main),
            end: new SphereObject(1, mat.main),
        };

        this._graph.root.add(this._userDragObjects.start);
        this._graph.root.add(this._userDragObjects.end);

        this._updateUserAxis();
        this._updateUserDragPointRadius();
    }

    setDraggedPoint(point) {
        if (this._draggedPoint === point) return;
        this._draggedPoint = point;
        this._updateUserDragPointColor();
    }

    setHoveredPoint(point) {
        if (this._hoveredPoint === point) return;
        this._hoveredPoint = point;
        this._updateUserDragPointColor();
    }

    updateInput(mouse, camera, controls) {
        this._updateOverlay(camera);
        this._updateSound(camera);

        let axisEnabled = this._params.graph.axisVisible.user
        this._userDragObjects.start.visible = axisEnabled;
        this._userDragObjects.end.visible = axisEnabled;

        if (!axisEnabled) {
            this.setDraggedPoint(null);
            return;
        }

        let raycaster;
        let intersection;

        if (this.visible) {
            raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(mouse.position, camera);

            intersection = raycaster.intersectObjects([this._userDragObjects.start, this._userDragObjects.end], true);
            intersection = intersection && intersection[0];
        }

        if (mouse.which.left) {
            if (!this._mouseDown) {
                this._mouseDown = true;

                if (intersection) {
                    for (let point in this._userDragObjects) {
                        if (intersection.object === this._userDragObjects[point].mesh) {
                            this.setDraggedPoint(point);

                            controls.enabled = false;
                            RenderManager.activeCamera.update = true;
                        }
                    }

                }
            }
        } else {
            if (this._mouseDown) {
                this._mouseDown = false;

                this.setDraggedPoint(null);

                if (!SlideManager.currentSlide.camera.disabled) {
                    controls.enabled = true;
                }
                RenderManager.activeCamera.update = true;
            }
        }

        let hovered = null;
        if (intersection) {
            for (let point in this._userDragObjects) {
                if (intersection.object === this._userDragObjects[point].mesh) {
                    hovered = point;
                }
            }
        }
        this.setHoveredPoint(hovered);

        if (this.visible && this._draggedPoint) {
            let plane = this._graph.dragPlane;

            plane.visible = true;
            let planeInt = raycaster.intersectObject(plane, false);
            plane.visible = false;

            planeInt = planeInt && planeInt[0];
            if (planeInt) {
                let graphInverse = new THREE.Matrix4().getInverse(this._graph.root.matrixWorld);

                let point = planeInt.point.clone().applyMatrix4(graphInverse);

                let userAxisPoint = this._userAxis[this._draggedPoint];
                userAxisPoint[0] = point.x;
                userAxisPoint[1] = -point.z;

                this._updateUserAxis();
            }
        }
    }

    _updateUserAxis() {
        let { start, end } = this._userAxis;
        let [sizeX, sizeY] = this._params.terrain.size;

        let startV = new THREE.Vector2(...start), endV = new THREE.Vector2(...end);
        let mid = startV.clone().lerp(endV, 0.5);
        let dir = endV.clone().sub(startV).normalize();

        let minStartDist = Infinity, maxEndDist = -Infinity;
        for (let j = 0; j < 2; j++) {
            for (let i = 0; i < 2; i++) {
                let cornerV = new THREE.Vector2(i * sizeX, j * sizeY);
                minStartDist = Math.min(minStartDist, dir.dot(cornerV));
                maxEndDist = Math.max(maxEndDist, dir.dot(cornerV));
            }
        }
        let midDist = dir.dot(mid);

        startV = mid.clone().add(dir.clone().multiplyScalar(minStartDist - midDist));
        endV = mid.clone().add(dir.clone().multiplyScalar(maxEndDist - midDist));

        this._graph.setUserAxis(startV.x, startV.y, endV.x, endV.y);

        for (let point in this._userDragObjects) {
            let position = this._userAxis[point];
            let object = this._userDragObjects[point];
            object.position.set(position[0], 0, -position[1]);
        }
    }

    setUserDragPointRadius(radius) {
        this._params.graph.style.userDragPointRadius = radius;

        this._updateUserDragPointRadius();
    }

    _updateUserDragPointRadius() {
        let { axisRadius, userDragPointRadius } = this._params.graph.style;

        for (let point in this._userDragObjects) {
            let object = this._userDragObjects[point];
            object.setRadius(axisRadius + userDragPointRadius);
        }
    }

    _updateUserDragPointColor() {
        let { main, highlighted } = this._userDragMaterials;
        let hlPoint = this._draggedPoint || this._hoveredPoint;

        for (let name in this._userDragObjects) {
            let object = this._userDragObjects[name]
            object.setMaterial(name === hlPoint ? highlighted : main);
        }
    }

    _initOverlay() {
        this._overlay = new Overlay();
        Q3.RenderManager.overlay2d.appendChild(this._overlay.domElement);

        this._overlayLabels = {};
        let params = [
            { name: 'speed', label: 'Speed' },
            { name: 'velX', label: 'Velocity' },
            { name: 'velY', label: 'Velocity' },
            { name: 'velUser', label: 'Velocity' },
        ];
        for (let { name, label } of params) {
            this._overlayLabels[name] = this._overlay.createLabel(label);
        }
    }

    _uninitOverlay() {
        Q3.RenderManager.overlay2d.removeChild(this._overlay.domElement);
    }

    _updateOverlay(camera) {
        this._overlay.update(camera);

        let carLabelPos = new THREE.Vector3().applyMatrix4(this._car.matrixWorld);
        let carLabelOffset = new THREE.Vector3(0, this._params.car.scale * 10, 0).applyMatrix4(this.matrixWorld);
        carLabelPos.add(carLabelOffset);

        let projectionLabelOffset = new THREE.Vector3(0, 1, 0).applyMatrix4(this.matrixWorld);
        let projectionPos = {};
        for (let axis of ['x', 'y', 'user']) {
            let pos = this._graph.getProjectionClosestPoint(axis).clone().applyMatrix4(this._graph.root.matrixWorld);
            pos.add(projectionLabelOffset);
            projectionPos[axis] = pos;
        }

        let graphParams = this._params.graph;
        let graphVisible = graphParams.visible;
        let graphProjectionVisible = graphVisible && graphParams.projectionVisible;
        let graphXAxisVisible = graphProjectionVisible && graphParams.axisVisible.x;
        let graphYAxisVisible = graphProjectionVisible && graphParams.axisVisible.y;
        let graphUserAxisVisible = graphProjectionVisible && graphParams.axisVisible.user;

        let labelsVisible = this._params.labels.visible;
        this._overlayLabels.speed.visible = labelsVisible.speed;
        this._overlayLabels.velX.visible = labelsVisible.velocityX && graphXAxisVisible;
        this._overlayLabels.velY.visible = labelsVisible.velocityY && graphYAxisVisible;
        this._overlayLabels.velUser.visible = labelsVisible.velocityUserAxis && graphUserAxisVisible;

        this._overlayLabels.speed.position.copy(carLabelPos);
        let speed = Math.round(this.speed * 30 * 3.6);
        this._overlayLabels.speed.setValue(speed);

        const skipZero = (vel, speed) => {
            if (speed !== 0) {
                if (vel < 0 && vel > -1) vel = -1;
                if (vel >= 0 && vel < 1) vel = 1;
            }
            return vel;
        };

        this._overlayLabels.velX.position.copy(projectionPos.x);
        let velX = skipZero(Math.round(this.velocityX * 30 * 3.6), speed);
        this._overlayLabels.velX.setValue(velX);

        this._overlayLabels.velY.position.copy(projectionPos.y);
        let velY = skipZero(Math.round(this.velocityY * 30 * 3.6), speed);
        this._overlayLabels.velY.setValue(velY);

        this._overlayLabels.velUser.position.copy(projectionPos.user);
        let velUser = skipZero(Math.round(this.velocityUserAxis * 30 * 3.6), speed);
        this._overlayLabels.velUser.setValue(velUser);
    }

    setLabelVisible(label, visible) {
        this._params.labels.visible[label] = visible;
    }

    _initSound() {
        this._engineSound = new EngineSound();
        this._car.add(this._engineSound.sound);
        this._updateVolume();
    }

    _uninitSound() {
        let parent = this._engineSound.listener.parent;
        if (parent) {
            parent.remove(this._engineSound.listener);
        }

        this._engineSound.dispose();
    }

    _updateSound(camera) {
        if (this._engineSound.listener.parent !== camera) {
            camera.add(this._engineSound.listener);
        }
        this._engineSound.setSpeed(this._speed);
        this._engineSound.updateVolume();
    }

    _updateVolume() {
        this._engineSound.setVolume(this._params.sound.volume);
        this._engineSound.setRefDistance(this._params.sound.refDistance);
    }

    setVolume(volume) {
        this._params.sound.volume = volume;
        this._updateVolume();
    }

    setRefDistance(distance) {
        this._params.sound.refDistance = distance;
        this._updateVolume();
    }
};

class Terrain extends THREE.Object3D {
    constructor(textureUrls, colors, params) {
        super()

        this._textureUrls = {
            ground: textureUrls.ground,
            //side: textureUrls.side,
        };

        this._colors = {
            ground: colors.ground,
            //side: colors.side,
        };

        this._params = {
            terrain: {
                size: [32, 32],
                segmentCount: 64,
                shape: {
                    frequency: [0.5, 0.5],
                    zScale: 0.5,
                },
            },
        };
        assignPrimitiveRecursive(this._params, params);

        this._buildMaterials();
        this._buildGeometries();



        this._objects = {
            ground: new THREE.Mesh(this._geometries.ground, this._materials.ground),
            //side: new THREE.Mesh(this._geometries.side, this._materials.side),
            //bottom: new THREE.Mesh(this._geometries.ground, this._materials.side),
        };

        this._objects.ground.rotation.x = -Math.PI * 0.5;
        this._objects.ground.receiveShadow = true;

        //this._objects.side.rotation.x = -Math.PI*0.5;

        //this._objects.bottom.position.y = -1;
        //this._objects.bottom.rotation.x = -Math.PI*0.5;

        //this.add(this._objects.ground, this._objects.side, this._objects.bottom);
        this.add(this._objects.ground);
    }

    getZ(x, y) {
        let { frequency: [fx, fy], zScale } = this._params.shape;
        return (Math.sin(x * fx) + Math.sin(y * fy)) * zScale;
    }

    _buildMaterials() {
        let groundMat = MaterialBuilder.build({
            color: this._colors.ground,
            materialType: "pbr",
            roughness: 1,
            metalness: 0,
        });
        groundMat.envMapIntensity = 0;

        /*let sideMat = MaterialBuilder.build({
            color: this._colors.side,
            materialType: "pbr",
            roughness: 1,
            metalness: 0,
            siding: "Both",
        });*/

        getTourReadyPromise().then(() => {
            let loader = new THREE.TextureLoader();

            loader.load(this._textureUrls.ground, texture => {
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;

                groundMat.color.setHex(0xffffff);
                groundMat.map = texture;
                groundMat.needsUpdate = true;
            });

            /*loader.load(this._textureUrls.side, texture => {
                texture.wrapS = THREE.RepeatWrapping;
                texture.wrapT = THREE.RepeatWrapping;

                sideMat.color.setHex(0xffffff);
                sideMat.map = texture;
                sideMat.needsUpdate = true;
            });*/
        });

        this._materials = {
            ground: groundMat,
            //side: sideMat,
        };
    }

    _buildGeometries() {
        let { size: [sizeX, sizeY], segmentCount } = this._params;
        let x0 = -sizeX * 0.5, x1 = sizeX * 0.5;
        let y0 = -sizeY * 0.5, y1 = sizeY * 0.5;



        let vertices = [], uvs = [];
        let indices = [];

        for (let yi = 0; yi <= segmentCount; yi++) {
            let ya = yi / segmentCount;
            let y = y0 + (y1 - y0) * ya;

            for (let xi = 0; xi <= segmentCount; xi++) {
                let xa = xi / segmentCount;
                let x = x0 + (x1 - x0) * xa;

                vertices.push(x, y, this.getZ(x, y));
                uvs.push(x * UvScale, y * UvScale);
            }
        }

        for (let yi = 0; yi < segmentCount; yi++) {
            let indexRow0 = yi * (segmentCount + 1);
            let indexRow1 = (yi + 1) * (segmentCount + 1);

            for (let xi = 0; xi < segmentCount; xi++) {
                let index0 = indexRow0 + xi, index1 = indexRow0 + xi + 1;
                let index2 = indexRow1 + xi, index3 = indexRow1 + xi + 1;
                indices.push(index0, index1, index2, index2, index1, index3);
            }
        }

        vertices = new Float32Array(vertices);
        uvs = new Float32Array(uvs);



        /*let sideVertices = [], sideUvs = [];
        let sideIndices = [];

        let index0;

        index0 = sideVertices.length/3;
        for (let xi = 0; xi <= segmentCount; xi++) {
            let xa = xi/segmentCount;
            let x = x0+(x1-x0)*xa;

            let z = this.getZ(x, y0);
            sideVertices.push(x, y0, z, x, y0, z-1);
            sideUvs.push(x*UvScale, z*UvScale, x*UvScale, (z-1)*UvScale);
        }

        for (let xi = 0; xi < segmentCount; xi++) {
            let indexi = index0+xi*2;
            sideIndices.push(indexi+0, indexi+1, indexi+2, indexi+2, indexi+1, indexi+3);
        }

        index0 = sideVertices.length/3;
        for (let xi = 0; xi <= segmentCount; xi++) {
            let xa = xi/segmentCount;
            let x = x1+(x0-x1)*xa;

            let z = this.getZ(x, y1);
            sideVertices.push(x, y1, z, x, y1, z-1);
            sideUvs.push(x*UvScale, z*UvScale, x*UvScale, (z-1)*UvScale);
        }

        for (let xi = 0; xi < segmentCount; xi++) {
            let indexi = index0+xi*2;
            sideIndices.push(indexi+0, indexi+1, indexi+2, indexi+2, indexi+1, indexi+3);
        }

        index0 = sideVertices.length/3;
        for (let yi = 0; yi <= segmentCount; yi++) {
            let ya = yi/segmentCount;
            let y = y0+(y1-y0)*ya;

            let z = this.getZ(x1, y);
            sideVertices.push(x1, y, z, x1, y, z-1);
            sideUvs.push(y*UvScale, z*UvScale, y*UvScale, (z-1)*UvScale);
        }

        for (let yi = 0; yi < segmentCount; yi++) {
            let indexi = index0+yi*2;
            sideIndices.push(indexi+0, indexi+1, indexi+2, indexi+2, indexi+1, indexi+3);
        }

        index0 = sideVertices.length/3;
        for (let yi = 0; yi <= segmentCount; yi++) {
            let ya = yi/segmentCount;
            let y = y1+(y0-y1)*ya;

            let z = this.getZ(x0, y);
            sideVertices.push(x0, y, z, x0, y, z-1);
            sideUvs.push(y*UvScale, z*UvScale, y*UvScale, (z-1)*UvScale);
        }

        for (let yi = 0; yi < segmentCount; yi++) {
            let indexi = index0+yi*2;
            sideIndices.push(indexi+0, indexi+1, indexi+2, indexi+2, indexi+1, indexi+3);
        }

        sideVertices = new Float32Array(sideVertices);
        sideUvs = new Float32Array(sideUvs);*/



        let groundGeo = new THREE.BufferGeometry();
        groundGeo.addAttribute('position', new THREE.BufferAttribute(vertices, 3));
        groundGeo.addAttribute('uv', new THREE.BufferAttribute(uvs, 2));
        groundGeo.setIndex(indices);
        groundGeo.computeVertexNormals();

        /*let sideGeo = new THREE.BufferGeometry();
        sideGeo.addAttribute('position', new THREE.BufferAttribute(sideVertices, 3));
        sideGeo.addAttribute('uv', new THREE.BufferAttribute(sideUvs, 2));
        sideGeo.setIndex(sideIndices);
        sideGeo.computeVertexNormals();*/



        this._geometries = {
            ground: groundGeo,
            //side: sideGeo,
        };
    }

    _rebuildGeometries() {
        this._geometries.ground.dispose();
        //this._geometries.side.dispose();

        this._buildGeometries();

        this._objects.ground.geometry = this._geometries.ground;
        //this._objects.side.geometry = this._geometries.side;
        //this._objects.bottom.geometry = this._geometries.ground;
    }

    setSize(sizeX, sizeY) {
        let size = this._params.size;
        size[0] = sizeX;
        size[1] = sizeY;
        this._rebuildGeometries();
    }

    setSegmentCount(count) {
        this._params.segmentCount = count;
        this._rebuildGeometries();
    }

    setShape(fx, fy, zScale) {
        let shape = this._params.shape;
        shape.frequency[0] = fx;
        shape.frequency[1] = fy;
        shape.zScale = zScale;
        this._rebuildGeometries();
    }
};

class Car extends THREE.Group {
    constructor() {
        super();

        this._car = new THREE.Group();
        this._base = new CarBase();

        this._wheels = [];
        this.wheelPositions = [];
        this.wheelRadius = null;
        this.frontAxlePosition = new THREE.Vector3();
        this.rearAxlePosition = new THREE.Vector3();
        this._wheelDriveRotation = 0;
        this._wheelSteerRotation = 0;

        this._model = null;

        this.add(this._car);
        this._car.add(this._base);

        this._scale = 1;
    }

    setWheelDriveRotation(rotation) {
        this._wheelDriveRotation = rotation;

        for (let wheel of this._wheels) {
            wheel.setDriveRotation(rotation);
        }
    }

    setWheelSteerRotation(rotation) {
        this._wheelSteerRotation = rotation;

        for (let id of this._model.frontWheels) {
            let wheel = this._wheels[id];
            wheel.setSteerRotation(rotation);
        }
    }

    _setWheelCount(count) {
        let oldCount = this._wheels.length;

        for (let i = oldCount; i < count; i++) {
            let wheel = new Wheel();
            this._wheels[i] = wheel;
            this._car.add(wheel);
            this.wheelPositions[i] = new THREE.Vector3();
        }

        for (let i = count; i < oldCount; i++) {
            let wheel = this._wheels[i];
            this._car.remove(wheel);
        }

        this._wheels.length = count;
        this.wheelPositions.length = count;
    }

    setModel(model) {
        this._base.setMesh(model.objects.main.clone());

        let wheelCount = model.objects.wheels.length;
        this._setWheelCount(wheelCount);

        for (let [i, wheelMesh] of model.objects.wheels.entries()) {
            let wheel = this._wheels[i];
            wheel.setMesh(wheelMesh.clone());
        }

        this._model = model;

        this._updateWheelInfo();
    }

    setScale(scale) {
        this._scale = scale;
        this._updateScale();
    }

    _updateScale() {
        let scale = this._scale;

        this._base.scale.set(scale, scale, scale);
        this._updateWheelInfo();
    }

    _updateWheelInfo() {
        let scale = this._scale;

        for (let [i, wheel] of this._wheels.entries()) {
            let modelWheelPos = this._model.wheelPositions[i];
            let wheelPos = this.wheelPositions[i];
            wheelPos.copy(modelWheelPos).multiplyScalar(scale);

            wheel.position.copy(wheelPos);
            wheel.scale.set(scale, scale, scale);
        }

        this.wheelRadius = this._model.wheelRadius * scale;
        this.frontAxlePosition.copy(this._model.frontAxlePosition).multiplyScalar(scale);
        this.rearAxlePosition.copy(this._model.rearAxlePosition).multiplyScalar(scale);
        this.originAboveAxles = this._model.originAboveAxles * scale;
    }
};

class CarBase extends THREE.Object3D {
    constructor() {
        super();

        this._mesh = null;
    }

    setMesh(mesh) {
        let oldMesh = this._mesh;
        if (oldMesh) {
            this.remove(oldMesh);
        }

        this._mesh = mesh;
        this.add(mesh);
    }
}

class Wheel extends THREE.Object3D {
    constructor() {
        super();

        this._rotator = new THREE.Object3D();
        this._rotator.rotation.order = 'YXZ';
        this._offset = new THREE.Object3D();

        this.add(this._rotator);
        this._rotator.add(this._offset);

        this._mesh = null;
    }

    setDriveRotation(rotation) {
        this._rotator.rotation.x = -rotation;
    }

    setSteerRotation(rotation) {
        this._rotator.rotation.y = -rotation;
    }

    setPivotOffset(offset) {
        this._rotator.position.x = -offset;
        this._offset.position.x = offset;
    }

    setMesh(mesh) {
        let oldMesh = this._mesh;
        if (oldMesh) {
            this._offset.remove(oldMesh);
        }

        this._mesh = mesh;
        this._offset.add(mesh);
    }
};

class CarModel {
    constructor(mesh, { modelScale, modelRotation, wheelNames, wheelPivotOffsets, frontWheels, rearWheels }) {
        this.modelScale = modelScale;
        this.modelRotation = modelRotation;

        mesh.scale.multiplyScalar(modelScale);
        mesh.rotation.copy(modelRotation);
        mesh.updateMatrixWorld();

        this.objects = {
            main: mesh,
            wheels: [],
        };

        let allIds = [];
        for (let [i, name] of wheelNames.entries()) {
            this.objects.wheels.push(mesh.getObjectByName(name));
            allIds.push(i);
        }

        let wheelDiameter = 0;
        this.wheelPositions = [];

        for (let [i, wheel] of this.objects.wheels.entries()) {
            let pivotOffset = wheelPivotOffsets[i];

            let [position, quaternion, scale] = [new THREE.Vector3(), new THREE.Quaternion(), new THREE.Vector3()];
            wheel.matrixWorld.decompose(position, quaternion, scale);
            position.x += pivotOffset;
            this.wheelPositions.push(position);
            wheel.position.set(-pivotOffset, 0, 0);
            wheel.quaternion.copy(quaternion);
            wheel.scale.copy(scale);

            if (wheel.parent) {
                wheel.parent.remove(wheel);
            }

            let geo = wheel.geometry;
            geo.computeBoundingBox();
            let size = geo.boundingBox.max.x - geo.boundingBox.min.x;
            size *= scale.x;
            wheelDiameter += size;
        }

        this.wheelRadius = wheelDiameter * 0.5 / this.objects.wheels.length;

        this.frontWheels = frontWheels.slice();
        this.rearWheels = rearWheels.slice();

        this.frontAxlePosition = this.getWheelsAveragePosition(frontWheels);
        this.rearAxlePosition = this.getWheelsAveragePosition(rearWheels);
        this.originAboveAxles = -this.getWheelsAveragePosition(allIds).y;
    }

    getWheelsAveragePosition(ids, pos = new THREE.Vector3()) {
        for (let id of ids) {
            let position = this.wheelPositions[id];
            pos.add(position);
        }

        pos.divideScalar(ids.length);
        return pos;
    }
};

class SphereObject extends THREE.Object3D {
    constructor(radius, material) {
        super();

        this._geometry = new THREE.IcosahedronBufferGeometry(1, 3);
        this._object = new THREE.Mesh(this._geometry, material);
        this.add(this._object);

        this.setRadius(radius);
    }

    setMaterial(material) {
        this._object.material = material;
    }

    setRadius(radius) {
        this._object.scale.set(1, 1, 1).multiplyScalar(radius);
    }

    get mesh() {
        return this._object;
    }
};

class CylinderObject extends THREE.Object3D {
    constructor(radius, height, material) {
        super();

        this._geometry = new THREE.CylinderBufferGeometry(1, 1, 1, 32);
        this._object = new THREE.Mesh(this._geometry, material);
        this.add(this._object);

        this._radius = radius;
        this._height = height;
        this._updateScale();
    }

    setRadius(radius) {
        this._radius = radius;
        this._updateScale();
    }

    setHeight(height) {
        this._height = height;
        this._updateScale();
    }

    _updateScale() {
        this._object.scale.set(this._radius, this._height, this._radius);
    }
};

class ConeObject extends THREE.Object3D {
    constructor(radius, height, material) {
        super();

        this._geometry = new THREE.ConeBufferGeometry(1, 1, 32);
        this._object = new THREE.Mesh(this._geometry, material);
        this.add(this._object);

        this._radius = radius;
        this._height = height;
        this._updateScale();
    }

    setRadius(radius) {
        this._radius = radius;
        this._updateScale();
    }

    setHeight(height) {
        this._height = height;
        this._updateScale();
    }

    setMaterial(material) {
        this._object.material = material;
    }

    _updateScale() {
        this._object.scale.set(this._radius, this._height, this._radius);
    }
};

class TorusObject extends THREE.Object3D {
    constructor(innerRadius, outerRadius, material) {
        super();

        this._innerRadius = innerRadius;
        this._outerRadius = outerRadius;
        this._buildGeometry();
        this._object = new THREE.Mesh(this._geometry, material);
        this.add(this._object);
    }

    _buildGeometry() {
        let { _innerRadius: inner, _outerRadius: outer } = this;
        let middle = (inner + outer) * 0.5;
        let thick = outer - middle;
        this._geometry = new THREE.TorusBufferGeometry(middle, thick, 32, 32);
    }

    _rebuildGeometry() {
        this._geometry.dispose();
        this._buildGeometry();
        this._object.geometry = this._geometry;
    }

    setInnerRadius(radius) {
        if (radius === this._innerRadius) return;

        this._innerRadius = radius;
        this._rebuildGeometry();
    }

    setOuterRadius(radius) {
        if (radius === this._outerRadius) return;

        this._outerRadius = radius;
        this._rebuildGeometry();
    }
};

class Graph {
    constructor() {
        this._labelGeo = {};

        this.root = new THREE.Group();

        let mat = this._materials = {
            origin: MaterialBuilder.build({ color: Colors.graph.origin, materialType: "pbr" }),
            xAxis: MaterialBuilder.build({ color: Colors.graph.xAxis, materialType: "pbr" }),
            yAxis: MaterialBuilder.build({ color: Colors.graph.yAxis, materialType: "pbr" }),
            userAxis: MaterialBuilder.build({ color: Colors.graph.userAxis, materialType: "pbr" }),
            projection: MaterialBuilder.build({ color: Colors.graph.projection, materialType: "pbr" }),
            projectionPoint: MaterialBuilder.build({ color: Colors.graph.projectionPoint, materialType: "pbr" }),
        };

        this._origin = new SphereObject(2, mat.origin);
        this._axes = {
            x: new GraphAxis(mat.xAxis),
            y: new GraphAxis(mat.yAxis),
            user: new GraphAxis(mat.userAxis),
        };

        this._labelOrigins = {
            x: new THREE.Group(),
            y: new THREE.Group(),
        };
        this._labels = {
            x: null,
            y: null,
        };

        this._projectionRoot = new THREE.Group();

        this._projections = {
            x: new AxisProjection(mat.projection),
            y: new AxisProjection(mat.projection),
            user: new AxisProjection(mat.projection),
        };
        this._projectionPointObject = new SphereObject(0.5, mat.projectionPoint);

        /*let ttfLoader = new THREE.TTFLoader();
        ttfLoader.load(fontUrl, (fontData) => {
            let font = new THREE.Font(fontData);

            let textParams = {
                font: font,
                size: 1,
                height: 0.1,
                curveSegments: 12,
            };

            this._labelGeo.x = new THREE.TextBufferGeometry('x', textParams);
            this._labelGeo.y = new THREE.TextBufferGeometry('y', textParams);
            this._labels.x = new THREE.Mesh(this._labelGeo.x, mat.xAxis);
            this._labels.y = new THREE.Mesh(this._labelGeo.y, mat.yAxis);
            this._labelOrigins.x.add(this._labels.x);
            this._labelOrigins.y.add(this._labels.y);
        });*/

        this._userAxis = {
            start: new THREE.Vector3(),
            end: new THREE.Vector3(),
        };

        this.root.add(this._origin);
        this.root.add(this._axes.x.root, this._axes.y.root, this._axes.user.root);
        this.root.add(this._labelOrigins.x, this._labelOrigins.y);
        this.root.add(this._projectionRoot);
        this._projectionRoot.add(this._projectionPointObject);
        this._projectionRoot.add(this._projections.x.root, this._projections.y.root, this._projections.user.root);

        this._size = [0, 0];
        this._projectionPoint = new THREE.Vector3();

        this._style = {};
        this.setStyle({
            originRadius: 1,
            axisRadius: 1,
            axisTipRadius: 1,
            axisTipHeight: 4,
            projectionLineRadius: 0.25,
            projectionPointRadius: 0.25,
        });

        this._dragPlaneGeometry = new THREE.PlaneBufferGeometry(1, 1);
        this._dragPlane = new THREE.Mesh(this._dragPlaneGeometry, mat.origin);
        this._dragPlane.visible = false;
        this.root.add(this._dragPlane);
    }

    getProjectionClosestPoint(axis) {
        return this._projections[axis].closestPoint;
    }

    get dragPlane() {
        return this._dragPlane;
    }

    setAxisEnabled(axis, enabled) {
        this._axes[axis].root.visible = enabled;
        this._projections[axis].root.visible = enabled;

        let label = this._labelOrigins[axis];
        if (label) label.visible = enabled;

        this._origin.visible = this._axes.x.root.visible || this._axes.y.root.visible;
    }

    setStyle(style) {
        Object.assign(this._style, style);
        let { originRadius, axisRadius, axisTipRadius, axisTipHeight, projectionLineRadius, projectionPointRadius } = this._style;

        for (let axis in this._axes) {
            this._axes[axis].setStyle({ line: axisRadius, tipRadius: axisTipRadius, tipHeight: axisTipHeight });
        }

        for (let axis in this._projections) {
            this._projections[axis].setStyle({ axis: axisRadius, projection: projectionLineRadius });
        }

        this._origin.setRadius(axisRadius + originRadius);
        this._projectionPointObject.setRadius(projectionLineRadius + projectionPointRadius);
    }

    setSize(sizeX, sizeY) {
        this._axes.x.setLine(new THREE.Vector3(0, 0, 0), new THREE.Vector3(sizeX, 0, 0));
        this._axes.y.setLine(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -sizeY));

        let { x: xLabel, y: yLabel } = this._labelOrigins;

        let labelSize = 4;

        xLabel.position.set(sizeX, 0, labelSize * 1.5);
        yLabel.position.set(-labelSize * 1.5, 0, -sizeY);
        xLabel.rotation.x = -Math.PI * 0.5;
        yLabel.rotation.x = -Math.PI * 0.5;
        xLabel.scale.set(1, 1, 1).multiplyScalar(labelSize);
        yLabel.scale.set(1, 1, 1).multiplyScalar(labelSize);

        this._dragPlane.position.set(sizeX * 0.5, 0, -sizeY * 0.5);
        this._dragPlane.rotation.x = -Math.PI * 0.5;
        this._dragPlane.scale.set(sizeX, sizeY, 1);

        this._size = [sizeX, sizeY];
        this._updateProjection();
    }

    setUserAxis(x0, y0, x1, y1) {
        let { start, end } = this._userAxis;

        start.set(x0, 0, -y0);
        end.set(x1, 0, -y1);

        this._axes.user.setLine(start, end);
        this._updateUserAxisProjection();
    }

    setProjectionVisible(visible) {
        this._projectionRoot.visible = visible;
    }

    setProjectionPoint(x, y) {
        this._projectionPoint.set(x, 0, -y);
        this._projectionPointObject.position.set(x, 0, -y);
        this._updateProjection();
    }

    _updateProjection() {
        let [sizeX, sizeY] = this._size;

        let origin = new THREE.Vector3();
        let xEnd = new THREE.Vector3(sizeX, 0, 0);
        let yEnd = new THREE.Vector3(0, 0, sizeY);
        let prPoint = this._projectionPoint;

        this._projections.x.setProjection(origin, xEnd, prPoint);
        this._projections.y.setProjection(origin, yEnd, prPoint);
        this._updateUserAxisProjection();
    }

    _updateUserAxisProjection() {
        let { start, end } = this._userAxis;
        let prPoint = this._projectionPoint;

        this._projections.user.setProjection(start, end, prPoint);
    }
};

class DomLabel3D {
    constructor(name) {
        this.position = new THREE.Vector3();
        this.domElement = document.createElement("div");
        this.nameElement = document.createElement("div");
        this.valueElement = document.createElement("div");
        this.numberElement = document.createElement("span");
        this.unitElement = document.createElement("span");
        this.domElement.classList.add("terrain-car-label");
        this.nameElement.classList.add("name");
        this.valueElement.classList.add("value");
        this.numberElement.classList.add("number");
        this.unitElement.classList.add("units");
        this.domElement.appendChild(this.nameElement);
        this.domElement.appendChild(this.valueElement);
        this.valueElement.appendChild(this.numberElement);
        this.valueElement.appendChild(this.unitElement);
        this.nameElement.innerHTML = name;
        this.unitElement.innerHTML = " km/h";
        this._value = 0;
        this.visible = true;
        this._updateValue();
    }

    setValue(value) {
        this._value = value;
        this._updateValue();
    }

    _updateValue() {
        this.numberElement.innerHTML = this._value;
        this.domElement.classList.toggle("negative", this._value < 0);
    }

    update(camera) {
        if (!this.visible) {
            this.domElement.style.display = "none";
        } else {
            let pos2d = this.position.clone().project(camera);
            this.domElement.style.display = pos2d.z >= 1 ? "none" : "block"
            this.domElement.style.left = "calc(" + (pos2d.x + 1) * 0.5 * 100 + "% - 64px)";
            this.domElement.style.top = "calc(" + (-pos2d.y + 1) * 0.5 * 100 + "% - 32px)";
        }
    }
};

class Overlay {
    constructor() {
        this.domElement = document.createElement("div");
        this.domElement.classList.add("terrain-car-overlay");
        this._labels = new Set();
    }

    setVisible(visible) {
        this.domElement.classList.toggle("hidden", !visible);
    }

    createLabel(name) {
        let label = new DomLabel3D(name);
        this._labels.add(label);
        this.domElement.appendChild(label.domElement);
        return label;
    }

    update(camera) {
        for (let label of this._labels) {
            label.update(camera);
        }
    }
};

class GraphAxis {
    constructor(material, hlMaterial) {
        this.root = new THREE.Group();

        this._materials = {
            main: material,
            highlighted: hlMaterial,
        };

        this._startPoint = new THREE.Group();
        this._line = new CylinderObject(1, 0, material);
        this._arrow = new ConeObject(2, 4, material);

        this.root.add(this._startPoint);
        this._startPoint.add(this._line, this._arrow);

        this._linePoints = {
            start: new THREE.Vector3(),
            end: new THREE.Vector3(),
        };

        this._style = {};
        this.setStyle({
            line: 1,
            tipRadius: 1,
            tipHeight: 4,
        });
    }

    setLine(start, end) {
        this._linePoints.start.copy(start);
        this._linePoints.end.copy(end);

        this._update();
    }

    setStyle(style) {
        Object.assign(this._style, style);
        let { line, tipRadius, tipHeight } = this._style;

        this._line.setRadius(line);
        this._arrow.setRadius(line + tipRadius);
        this._arrow.setHeight(tipHeight);

        this._update();
    }

    _update() {
        let { tipHeight } = this._style;
        let { start, end } = this._linePoints;

        let offset = end.clone().sub(start);
        let length = offset.length();
        let dir0 = new THREE.Vector3(0, 1, 0);
        let dir1 = offset.clone().normalize();

        this._line.setHeight(length);
        this._line.position.y = length * 0.5;

        this._arrow.position.y = length + tipHeight * 0.5;

        this._startPoint.position.copy(start);
        this._startPoint.quaternion.setFromUnitVectors(dir0, dir1);
    }

    setTipHighlighted(highlighted) {
        this._arrow.setMaterial(highlighted ? this._materials.highlighted : this._materials.main);
    }
};

class AxisProjection {
    constructor(material) {
        this.root = new THREE.Group();

        this._ring = new TorusObject(1, 1.5, material);
        this._line = new CylinderObject(0.25, 0, material);

        this.root.add(this._ring, this._line);

        this._projectionPoints = {
            start: new THREE.Vector3(),
            end: new THREE.Vector3(),
            point: new THREE.Vector3(),
        };
        this._closestPoint = new THREE.Vector3();
        this._style = {};
        this.setStyle({
            axis: 1,
            projection: 0.25,
        });
    }

    get closestPoint() {
        return this._closestPoint
    }

    setProjection(start, end, point) {
        let points = this._projectionPoints;
        points.start.copy(start);
        points.end.copy(end);
        points.point.copy(point);

        this._update();
    }

    setStyle(style) {
        Object.assign(this._style, style);
        let { axis, projection } = this._style;

        this._ring.setInnerRadius(axis);
        this._ring.setOuterRadius(axis + projection * 2);
        this._line.setRadius(projection);

        this._update();
    }

    _update() {
        let { start, end, point } = this._projectionPoints;
        let { axis, projection } = this._style;

        let line = new THREE.Line3(start, end);
        let closestPoint = line.closestPointToPoint(point, false, this._closestPoint);

        let dirl0 = new THREE.Vector3(0, 0, 1);
        let dirl1 = end.clone().sub(start).normalize();
        let dirp0 = new THREE.Vector3(0, 1, 0);
        let dirp1 = point.clone().sub(closestPoint).normalize();

        let towardsPoint = dirp1.clone().multiplyScalar(axis + projection).add(closestPoint);
        let length = towardsPoint.distanceTo(point);
        let midPoint = towardsPoint.clone().lerp(point, 0.5);

        this._ring.position.copy(closestPoint);
        this._ring.quaternion.setFromUnitVectors(dirl0, dirl1);
        this._line.position.copy(midPoint);
        this._line.quaternion.setFromUnitVectors(dirp0, dirp1);
        this._line.setHeight(length);
    }

};

function getUserActionPromise() {
    return new Promise((resolve, reject) => {
        let actionFunction;

        actionFunction = () => {
            window.removeEventListener("mousedown", actionFunction);
            window.removeEventListener("touchstart", actionFunction);
            resolve();
        };

        window.addEventListener("mousedown", actionFunction);
        window.addEventListener("touchstart", actionFunction);
    });
};

function promiseLoadAudio(loader, url) {
    return new Promise((resolve, reject) => {
        loader.load(url, resolve);
    });
}

class EngineSound {
    constructor() {
        this.listener = new THREE.AudioListener();
        this.sound = new THREE.PositionalAudio(this.listener);

        this._enabled = true;
        this._volume = 1;
        this._refDistance = 0.3;

        getTourReadyPromise().then(() => {
            let audioLoader = new THREE.AudioLoader();
            let loadPromise = promiseLoadAudio(audioLoader, getPersistentUrl(soundUrl));
            let actionPromise = getUserActionPromise();

            Promise.all([loadPromise, actionPromise]).then(([buffer, _]) => {
                this.sound.setBuffer(buffer);
                this.sound.setLoop(true);
                this.sound.play();
            });
        });
    }

    dispose() {

    }

    setSpeed(speed) {
        this.sound.setPlaybackRate(1 + speed * 3);
    }

    setEnabled(enabled) {
        this._enabled = enabled;
        this.updateVolume();
    }

    setVolume(volume) {
        this._volume = volume;
        this.updateVolume();
    }

    setRefDistance(distance) {
        this._refDistance = distance;
        this.updateVolume();
    }

    updateVolume() {
        if (!this._enabled) {
            this.sound.setVolume(0);
            return;
        }

        let isVisible = true;
        let object = this.sound;
        while (true) {
            if (!object || !object.visible) {
                isVisible = false;
                break;
            }

            if (object === RenderManager.activeScene) {
                break;
            }

            object = object.parent;
        }

        this.sound.setVolume(isVisible ? this._volume : 0);
        this.sound.setRefDistance(this._refDistance);
    }
};



class TexturePromiseLoader {
    constructor() {
        this._loader = new THREE.TextureLoader();
        this._textures = new Map();
    }

    load(url) {
        let promise = this._textures.get(url);

        if (!promise) {
            promise = new Promise((resolve, reject) => {
                this._loader.load(url, resolve, undefined, reject);
            });
            this._textures.set(url, promise);
        }

        return promise;
    }
};

class FBXPromiseLoader {
    constructor() {
        this._loader = new FBXLoader();
    }

    load(url) {
        return new Promise((resolve, reject) => {
            this._loader.load(url, resolve, undefined, reject);
        });
    }
}

class CarModelLoader {
    constructor(urlBase) {
        this._urlBase = urlBase;
        this._texLoader = new TexturePromiseLoader();
        this._fbxLoader = new FBXPromiseLoader();

        this._models = new Map();
    }

    load(params) {
        let promise = this._models.get(params);

        if (!promise) {
            promise = new Promise((resolve, reject) => {
                let textureIndices = {};

                let promises = [];
                promises.push(this._fbxLoader.load(this._urlBase + params.modelName));
                for (let name in params.textures) {
                    let url = this._urlBase + params.textures[name];
                    textureIndices[name] = promises.length;
                    promises.push(this._texLoader.load(url));
                }

                Promise.all(promises).then(values => {
                    let object = values[0];
                    let textures = {};
                    for (let name in textureIndices) {
                        textures[name] = values[textureIndices[name]];
                    }

                    let material = MaterialBuilder.build({ color: params.material.color });

                    for (let name in params.material.parameters) {
                        material[name] = params.material.parameters[name];
                    }
                    for (let name in params.material.textures) {
                        material[name] = textures[params.material.textures[name]];
                    }

                    object.traverse(obj => {
                        if (obj.material) obj.material = material;

                        if (obj.isMesh) {
                            obj.castShadow = true;
                        }
                    });

                    let carModel = new CarModel(object, params.parameters);
                    resolve(carModel);
                }, reject);
            });
            this._models.set(params, promise);
        }

        return promise;
    }
};

let tourReadyPromise;

function getTourReadyPromise() {
    if (!tourReadyPromise) {
        tourReadyPromise = new Promise((resolve, reject) => {
            if (Q3.isReady) {
                resolve();
            } else {
                listen("tourReady", () => resolve());
            }
        });
    }

    return tourReadyPromise;
}

let carModelLoader;

function loadModel(name) {
    carModelLoader = carModelLoader || new CarModelLoader(getPersistentUrl(modelUrlBase));

    return getTourReadyPromise().then(() => {
        return carModelLoader.load(CarModels[name])
    });
}



export default TerrainCar;
export { TerrainCar, updateTerrainCars };
